import { safeReturnPath } from "@bindery/shared/auth/returnPath";
import { Button } from "@bindery/ui/components/ui/button";
import { Link, useSearchParams } from "react-router";
import { type ReaderEntry, readerEntry } from "@/auth/links";
import { ReaderEntryButton } from "@/auth/reader-entry-button";
import { lastReader } from "@/storage/device-readers";
import { readNickname } from "@/storage/nickname";
import { SIGN_OUT_PATH } from "./sign-out";

export interface KeepReading {
  entry: ReaderEntry;
  label: string;
}

export interface SelectReaderLinks {
  keepReading: KeepReading | null;
  newReader: ReaderEntry;
}

// A sign-in that ends without a reader replaced the device's
// reader_session; returning to that reader is what keeps its unsent
// outbox rows flushable. The page never writes a reader into a cookie.
export function selectReaderLinks(
  returnTo: string,
  deviceReaderId: string | null = lastReader(),
  nicknameOf: (readerId: string) => string | null = readNickname,
): SelectReaderLinks {
  return {
    keepReading:
      deviceReaderId === null
        ? null
        : {
            entry: readerEntry(deviceReaderId, returnTo),
            label: `Keep reading as ${nicknameOf(deviceReaderId) ?? "this device's reader"}`,
          },
    newReader: readerEntry(null, returnTo),
  };
}

export function SelectReaderPage() {
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnPath(searchParams.get("returnTo"));
  const { keepReading, newReader } = selectReaderLinks(
    returnTo === "/" ? "/reader" : returnTo,
  );
  return (
    <main className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-4 p-8">
      <h1 className="text-2xl font-semibold">Choose a reader</h1>
      <p className="text-muted-foreground">
        You are signed in, but no reader is attached to this session yet.
      </p>
      {keepReading && (
        <ReaderEntryButton
          entry={keepReading.entry}
          className="min-h-10 w-full"
        >
          {keepReading.label}
        </ReaderEntryButton>
      )}
      <Button
        variant={keepReading ? "outline" : "default"}
        className="min-h-10 w-full"
        disabled
      >
        Choosing a reader from your account is coming soon
      </Button>
      <ReaderEntryButton
        entry={newReader}
        variant="ghost"
        className="min-h-10 w-full"
      >
        Read as a new reader without an account
      </ReaderEntryButton>
      <Link
        to={SIGN_OUT_PATH}
        className="text-muted-foreground self-center text-sm underline"
      >
        Sign out
      </Link>
    </main>
  );
}
